// La ÚNICA lectura que arma la tabla del Catálogo: `GET /portal/catalog`, el BFF del portal en
// retail-wizard. El front acumula páginas y filtra, ordena y cuenta sobre lo acumulado.
import { useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { wizardApi } from '@/api/services/retail-wizard';
import type { PortalCatalogPage, PortalCatalogRow } from '@/api/services/retail-wizard/contract';
import { mustRestartCatalog } from '../lib/catalogList';

export const PORTAL_CATALOG_KEY = ['sales-portal', 'catalog'] as const;
const KEY = PORTAL_CATALOG_KEY;

/** Filas por página del servidor, las mismas que Admin SK · Clientes. */
export const PORTAL_CATALOG_PAGE_SIZE = 25;

const STALE_TIME_MS = 60_000;

const NO_ROWS: PortalCatalogRow[] = [];

export interface PortalCatalogResult {
    /** Todas las páginas aplanadas, con referencia estable entre renders. */
    rows: PortalCatalogRow[];
    /** La primera página: la única que trae planes por nivel y promociones de la tienda. */
    firstPage: PortalCatalogPage | undefined;
    pagesLoaded: number;
    /** La primera página todavía viaja. */
    isPending: boolean;
    /** Falla de la PRIMERA página: sin nada cargado no hay tabla. */
    error: unknown;
    retry: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
    /** Por qué falló la página siguiente; lo cargado se queda. */
    nextPageError: unknown;
    /** El cursor venció: la lista se vuelve a pedir desde la primera página. */
    mustRestart: boolean;
    restart: () => void;
}

export function usePortalCatalog(): PortalCatalogResult {
    const queryClient = useQueryClient();
    const query = useInfiniteQuery({
        queryKey: KEY,
        queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
            wizardApi.listPortalCatalog({ limit: PORTAL_CATALOG_PAGE_SIZE, nextToken: pageParam }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last.nextToken,
        staleTime: STALE_TIME_MS,
    });

    // Memo obligatorio: TanStack Table compara `data` por referencia.
    const rows = useMemo(
        () => query.data?.pages.flatMap((page) => page.items) ?? NO_ROWS,
        [query.data],
    );
    const nextPageError = query.isFetchNextPageError ? query.error : null;

    return {
        rows,
        firstPage: query.data?.pages[0],
        pagesLoaded: query.data?.pages.length ?? 0,
        isPending: query.isPending,
        error: query.data ? null : query.error,
        retry: () => {
            void query.refetch();
        },
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        fetchNextPage: () => {
            void query.fetchNextPage();
        },
        nextPageError,
        mustRestart: mustRestartCatalog(nextPageError),
        restart: () => {
            void queryClient.resetQueries({ queryKey: KEY, exact: true });
        },
    };
}
